'use client';

import * as React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from 'recharts';

import { Skeleton } from '@workspace/ui/components/skeleton';
import { type WeddingSampleDto } from '~/data/budget-explorer/get-public-wedding-samples';

interface TotalSpendHistogramProps {
  samples: WeddingSampleDto[];
  isLoading?: boolean;
  highlightValue?: number;
  onBucketClick?: (bucket: { min: number; max: number; samples: WeddingSampleDto[] }) => void;
}

interface BucketData {
  range: string;
  min: number;
  max: number;
  count: number;
  percentage: number;
  samples: WeddingSampleDto[];
}

const BUCKET_COUNT = 15;

const COLORS = {
  primary: '#3b82f6',
  primaryLight: '#60a5fa',
  highlight: '#10b981',
  highlightLight: '#34d399',
  grid: '#6b7280',
  text: '#9ca3af',
  border: '#374151',
};

function createHistogramData(samples: WeddingSampleDto[]): BucketData[] {
  if (samples.length === 0) return [];

  const spends = samples.map((s) => s.totalSpend);
  const minSpend = Math.min(...spends);
  const maxSpend = Math.max(...spends);
  
  const roundedMin = Math.floor(minSpend / 5000) * 5000;
  const roundedMax = Math.ceil(maxSpend / 5000) * 5000;
  const bucketSize = (roundedMax - roundedMin) / BUCKET_COUNT;

  const buckets: BucketData[] = [];

  for (let i = 0; i < BUCKET_COUNT; i++) {
    const bucketMin = roundedMin + i * bucketSize;
    const bucketMax = bucketMin + bucketSize;
    const bucketSamples = samples.filter(
      (s) => s.totalSpend >= bucketMin && (i === BUCKET_COUNT - 1 ? s.totalSpend <= bucketMax : s.totalSpend < bucketMax)
    );

    buckets.push({
      range: `$${(bucketMin / 1000).toFixed(0)}k`,
      min: bucketMin,
      max: bucketMax,
      count: bucketSamples.length,
      percentage: Math.round((bucketSamples.length / samples.length) * 100),
      samples: bucketSamples,
    });
  }

  return buckets;
}

function formatTooltipValue(value: number): string {
  return `${value} wedding${value !== 1 ? 's' : ''}`;
}

export function TotalSpendHistogram({
  samples,
  isLoading,
  highlightValue,
  onBucketClick,
}: TotalSpendHistogramProps): React.JSX.Element {
  const data = React.useMemo(() => createHistogramData(samples), [samples]);

  const handleBarClick = React.useCallback(
    (bucketData: BucketData) => {
      if (onBucketClick && bucketData.count > 0) {
        onBucketClick({
          min: bucketData.min,
          max: bucketData.max,
          samples: bucketData.samples,
        });
      }
    },
    [onBucketClick]
  );

  if (isLoading) {
    return (
      <div className="h-[400px] flex items-center justify-center">
        <Skeleton className="w-full h-full" />
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="h-[400px] flex items-center justify-center text-muted-foreground">
        No data available. Try adjusting your filters.
      </div>
    );
  }

  const highlightBucketIndex = highlightValue
    ? data.findIndex((d) => highlightValue >= d.min && highlightValue < d.max)
    : -1;

  return (
    <div className="h-[400px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
          barCategoryGap="10%"
        >
          <defs>
            <linearGradient id="histogramBarGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={COLORS.primary} stopOpacity={0.9} />
              <stop offset="100%" stopColor={COLORS.primaryLight} stopOpacity={0.7} />
            </linearGradient>
            <linearGradient id="histogramBarGradientHighlight" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={COLORS.highlight} stopOpacity={1} />
              <stop offset="100%" stopColor={COLORS.highlightLight} stopOpacity={0.8} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={`${COLORS.grid}33`} vertical={false} />
          <XAxis
            dataKey="range"
            tick={{ fontSize: 12, fill: COLORS.text }}
            tickLine={false}
            axisLine={{ stroke: COLORS.border }}
            angle={-45}
            textAnchor="end"
            height={60}
            interval={0}
          />
          <YAxis
            tick={{ fontSize: 12, fill: COLORS.text }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(value) => value.toString()}
            label={{
              value: 'Number of Weddings',
              angle: -90,
              position: 'insideLeft',
              style: { textAnchor: 'middle', fontSize: 12, fill: COLORS.text },
            }}
          />
          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const bucketData = payload[0].payload as BucketData;
              return (
                <div className="rounded-lg border bg-popover text-popover-foreground p-3 shadow-lg">
                  <p className="font-medium">
                    ${(bucketData.min / 1000).toFixed(0)}k - ${(bucketData.max / 1000).toFixed(0)}k
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {formatTooltipValue(bucketData.count)} ({bucketData.percentage}%)
                  </p>
                  {bucketData.count > 0 && onBucketClick && (
                    <p className="text-xs text-primary mt-1">
                      Click to view details →
                    </p>
                  )}
                </div>
              );
            }}
          />
          {highlightValue && highlightBucketIndex >= 0 && (
            <ReferenceLine
              x={data[highlightBucketIndex]?.range}
              stroke={COLORS.highlight}
              strokeWidth={2}
              strokeDasharray="5 5"
              label={{
                value: 'Your Budget',
                position: 'top',
                fill: COLORS.highlight,
                fontSize: 12,
              }}
            />
          )}
          <Bar
            dataKey="count"
            radius={[4, 4, 0, 0]}
            animationDuration={800}
            animationEasing="ease-out"
            onClick={(barData) => {
              if (barData && onBucketClick) {
                handleBarClick(barData as BucketData);
              }
            }}
            style={{ cursor: onBucketClick ? 'pointer' : 'default' }}
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={
                  index === highlightBucketIndex
                    ? 'url(#histogramBarGradientHighlight)'
                    : 'url(#histogramBarGradient)'
                }
                style={{ cursor: onBucketClick && entry.count > 0 ? 'pointer' : 'default' }}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

